import gql from 'graphql-tag';
import { useCallback } from 'react';

import { useAdminClientListViewQuery, useDeleteClientMutation } from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PageLoading } from '@/ui/PageLoading';
import { usePrevious } from '@/utils/helpers/react.helper';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import AdminClientListView from './AdminClientListView';
import { AdminClientListItemType } from './columns';
import { useAdminClientListQueryParams } from './hooks/useAdminClientListQueryParams';

gql`
  query AdminClientListView(
    $paginationArgs: PaginationArgs!
    $orderByArgs: OrderByArgs
    $where: ClientWhereInput
  ) {
    listClients(paginationArgs: $paginationArgs, orderByArgs: $orderByArgs, where: $where) {
      edges {
        node {
          ...ClientFields
          meetings {
            edges {
              node {
                ...MeetingFields
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const PAGE_SIZE = 15;
export const clientTablePrefix = 'clients';

export default function AdminClientListClient() {
  const { paginationArgs, orderByArgs, where } = useAdminClientListQueryParams(
    PAGE_SIZE,
    clientTablePrefix
  );
  const clients = useAdminClientListViewQuery({
    variables: {
      paginationArgs,
      orderByArgs,
      where,
    },
  });
  const previousData = usePrevious(clients.data);
  const pagination = usePaginationState(PAGE_SIZE, clientTablePrefix);
  const [deleteClientMutation] = useDeleteClientMutation();

  useEntitySubscription('Client', {
    onCreate: {
      variables: {
        where,
      },
      onData: ({ data }) => {
        const obj = data.data?.onCreateClient;
        if (!obj) {
          captureSentry({
            title: 'AdminClientListClient | onData | onCreateClient | obj is undefined',
            detail: {},
          });
          return;
        }
        void clients.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: ({ data }) => {
        const obj = data.data?.onDeleteClient;
        if (!obj) {
          captureSentry({
            title: 'AdminClientListClient | onData | onDeleteClient | obj is undefined',
            detail: {},
          });
          return;
        }
        void clients.refetch();
      },
    },
  });

  const handleDeleteClient = useCallback(
    (row: AdminClientListItemType) => {
      void deleteClientMutation({
        variables: {
          where: {
            id: row.id,
          },
        },
      });
    },
    [deleteClientMutation]
  );

  if (!previousData && !clients.data) {
    return <PageLoading />;
  }

  return (
    <AdminClientListView
      data={clients.data || previousData!}
      pagination={pagination}
      handleDeleteClient={handleDeleteClient}
      tablePrefix={clientTablePrefix}
    />
  );
}
